import * as React from 'react'
import { Layout } from 'react-admin'
import { useUsagePageViews } from '../analytics/useUsagePageViews'
import ForceReloadDialog from '../components/ForceReloadDialog'
import UpdateAvailableBanner from '../components/UpdateAvailableBanner'
import VersionBadge from '../components/VersionBadge'
import { WhatsNewNotice } from '../releaseNotes'
import AppBar from './AppBar'
import Menu from './Menu'
import { Sidebar } from './Sidebar'

// Renders nothing; records one usage event per route change (vic/vetpo#49).
const UsagePageViews = () => {
  useUsagePageViews()
  return null
}

export default ({ children }: { children: React.ReactNode }) => (
  <>
    <Layout
      appBar={AppBar}
      menu={Menu}
      sidebar={Sidebar}
      // <main class="RaLayout-contentWithSidebar"> is the flex sibling of the
      // sidebar. Without minWidth:0 it grows to its children's min-content width
      // instead of shrinking to the available space, so MUI Grid gutter bleed /
      // wide charts cause horizontal page overflow on phones. minWidth:0 lets it
      // shrink; overflowX:clip trims any residual bleed. Invisible when the content
      // already fits. (The root/appFrame pin below is different: it applies at EVERY
      // width, desktop included.)
      //
      // The clamp must start at the very top of the layout: react-admin sets
      // `min-width: fit-content` on RaLayout-root and the flex default
      // `min-width: auto` on RaLayout-appFrame. Those make the *whole frame*
      // adopt its content's intrinsic (min-content) width — measured at 476px on
      // a 390px phone — so the page overflowed ~86px regardless of the inner
      // overrides below (the floor was bubbling up from the root, not escaping
      // the content box). Pinning the root + appFrame to minWidth:0 /
      // maxWidth:100% lets the frame track the viewport so the Plotly glucose
      // trend chart resizes to the phone container instead of inflating
      // scrollWidth.
      // The root/appFrame pin applies at EVERY width. It used to be gated
      // below md because, applied globally, it turned desktop's page-level
      // horizontal scrollbar (wide datagrids) into a silent clip — rightmost
      // columns unreachable. The clip is gone now that datagrids scroll
      // inside their own card (theme: RaDatagrid tableWrapper overflowX
      // auto), so the page itself never scrolls sideways: the Pets list used
      // to drag the whole app frame 466px (1024) / 50px (1440) wide.
      // Verified by a sweep of every route at 1024/1280/1440: no page
      // overflow and nothing hidden in the clipped main area.
      sx={{
        minWidth: 0,
        maxWidth: '100%',
        '& .RaLayout-appFrame': { minWidth: 0, maxWidth: '100%' },
        '& .RaLayout-contentWithSidebar': { minWidth: 0, overflowX: 'clip' },
        // ONE page gutter for every screen. Content used to get 8px and each
        // page added its own 0-49px on top (measured 2026-09-28: tables at
        // 63px, Clients at 79, Pet Shares at 104), so nothing lined up and a
        // 390px phone lost 98px to padding on some pages. Pages must not add
        // outer padding of their own.
        '& .RaLayout-content': {
          minWidth: 0,
          px: { xs: 2, md: 3 },
          pt: { xs: 2, md: 3 },
          pb: 4,
        },
      }}
    >
      {children}
    </Layout>
    {/* TODO: Re-enable AI chat when ready */}
    {/* <LiveChatWidget position="bottom-left" /> */}
    <VersionBadge />
    <UpdateAvailableBanner />
    <ForceReloadDialog />
    <WhatsNewNotice />
    <UsagePageViews />
  </>
)
